import React, { useState } from 'react';
import { calendarType, displayDate, displayRange, sortedBreaks } from './calendarPresentation';
import SchoolCalendarBreaks from './SchoolCalendarBreaks';
import './schoolCalendar.scss';
import { useTranslation } from 'react-i18next';

import { monthNames, weekdayNames } from '../../i18n/calendarLabels';

export default function SchoolCalendarView({ calendar }) {
  const { t: tr, i18n } = useTranslation();
  const months = monthNames(i18n.resolvedLanguage).map(name => name[0].toUpperCase() + name.slice(1));
  const weekdays = weekdayNames(i18n.resolvedLanguage);
  const [selectedDate, setSelectedDate] = useState(null);
  const breaks = sortedBreaks(calendar.breaks);
  const namesOn = date => breaks.filter(item => item.fromDate <= date && date <= item.tillDate).map(item => item.name).join(', ');
  const selectedNames = selectedDate && namesOn(selectedDate);
  const period = calendar.teachingPeriod;
  return (
    <div className="school-calendar">
      <header className="school-calendar-panel">
        <span className="school-calendar-readonly">{tr("calendar:readOnly_173")}</span>
        <h2>{tr("common:schoolCalendar")}</h2>
        <p>{tr("common:schoolYear")} {calendar.schoolYearStart}/{calendar.schoolYearStart + 1}</p>
        {calendar.integrationPending && <p>{tr("calendar:informationalCalendarTheseDatesDoNotYetAffectThe_162")}</p>}
        {calendar.status === 'updating' && <p>{tr("calendar:theCalendarIsBeingUpdatedTheLastPublishedVersion_174")}</p>}
        {period ? <p>{tr("calendar:teachingPeriod_164")} <strong>{displayRange(period, i18n.language)}</strong></p>
          : <p>{tr("calendar:theCalendarForThisYearHasNotBeenConfigured_165")}</p>}
      </header>
      {period && (
        <div className="school-calendar-layout">
          <section className="school-calendar-year school-calendar-panel" aria-label={tr("calendar:schoolYearCalendar_175")}>
            <p className="school-calendar-legend"><span>{tr("calendar:breaksAndHolidays_150")}</span><span>{tr("calendar:outsideTheTeachingPeriod_176")}</span></p>
            <div className="school-calendar-months">
              {Array.from({ length: 12 }, (_, index) => {
                const month = (index + 8) % 12;
                const year = calendar.schoolYearStart + (index >= 4 ? 1 : 0);
                const first = new Date(Date.UTC(year, month, 1));
                const offset = (first.getUTCDay() + 6) % 7;
                const count = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
                const rows = Math.ceil((offset + count) / 7);
                return (
                  <table key={`${year}-${month}`} className="school-calendar-month">
                    <caption>{months[month]} {year}</caption>
                    <thead><tr>{weekdays.map(day => <th key={day} scope="col">{day}</th>)}</tr></thead>
                    <tbody>{Array.from({ length: rows }, (_, week) => (
                      <tr key={`${year}-${month}-${week}`}>
                        {weekdays.map((weekday, dayIndex) => {
                          const day = week * 7 + dayIndex - offset + 1;
                          if (day < 1 || day > count) return <td key={weekday} />;
                          const date = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                          const names = namesOn(date);
                          const outside = date < period.fromDate || date > period.tillDate;
                          return (
                            <td key={weekday} className={outside ? 'school-calendar-outside' : ''}>
                              {names ? (
                                <button type="button" aria-label={`${displayDate(date, i18n.language)} — ${names}`} aria-pressed={date === selectedDate}
                                  className="school-calendar-break-day" onClick={() => setSelectedDate(date)}>{day}</button>
                              ) : <time dateTime={date} title={outside ? tr("calendar:outsideTheTeachingPeriod_176") : undefined}>{day}</time>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}</tbody>
                  </table>
                );
              })}
            </div>
            <p role="status" aria-live="polite" className="school-calendar-selection">
              {selectedNames ? `${displayDate(selectedDate, i18n.language)} — ${selectedNames}` : tr("calendar:selectAMarkedDayToSeeTheNameOf_177")}
            </p>
          </section>
          <aside className="school-calendar-list school-calendar-panel" aria-label={tr("calendar:breaksAndHolidays_150")}>
            <h3>{tr("calendar:breaksAndHolidays_150")}</h3>
            <SchoolCalendarBreaks calendar={calendar} />
          </aside>
        </div>
      )}
    </div>
  );
}
SchoolCalendarView.propTypes = { calendar: calendarType.isRequired };
